import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCalendarX,
  lucideCircleCheck,
  lucideRotateCcw,
  lucideTicketCheck,
} from '@ng-icons/lucide';
import { HlmBadgeImports, type BadgeVariants } from '@spartan-ng/helm/badge';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { formatCurrencyWithSymbol } from '../../utils/currency.utils';
import { Voucher, VoucherProductData, VoucherService } from '../../services/voucher.service';

/**
 * What a voucher is worth right now. Shared by /redeem (shop staff, logged in) and
 * /verify (opened from the QR code), so both show the voucher the same way.
 * Anything page-specific (e.g. the network note) is projected below the figures.
 */
@Component({
  selector: 'app-voucher-balance-card',
  standalone: true,
  imports: [
    NgIcon,
    HlmBadgeImports,
    HlmCardImports,
    HlmItemImports,
    HlmSeparatorImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({ lucideCalendarX, lucideCircleCheck, lucideRotateCcw, lucideTicketCheck }),
  ],
  templateUrl: './voucher-balance-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class VoucherBalanceCardComponent {
  private readonly voucherService = inject(VoucherService);

  readonly code = input.required<string>();
  /** Shop name on /verify, recipient on /redeem. */
  readonly subtitle = input<string | null>(null);
  readonly status = input.required<Voucher['status']>();
  readonly amount = input.required<number>();
  readonly initialAmount = input.required<number>();
  readonly product = input<VoucherProductData | null | undefined>(null);

  protected readonly money = formatCurrencyWithSymbol;

  protected readonly productImageUrl = computed(() =>
    this.voucherService.getProductImageUrl(this.product()?.image_cropped_path ?? null),
  );

  protected readonly badge = computed(
    (): {
      label: string;
      icon: string;
      variant: BadgeVariants['variant'];
    } => {
      switch (this.status()) {
        case 'redeemed':
          return { label: 'Eingelöst', icon: 'lucideTicketCheck', variant: 'secondary' };
        case 'expired':
          return { label: 'Abgelaufen', icon: 'lucideCalendarX', variant: 'destructive' };
        case 'refunded':
          return { label: 'Erstattet', icon: 'lucideRotateCcw', variant: 'destructive' };
        default:
          return { label: 'Aktiv', icon: 'lucideCircleCheck', variant: 'solid' };
      }
    },
  );
}
